import axios, { AxiosResponse } from 'axios';

const buildUrl = (base: string, params: Record<string, string | undefined>) => {
    const qs = Object.entries(params).filter(([, v]) => v).map(([k, v]) => `${k}=${v}`).join('&');
    return qs ? `${base}?${qs}` : base;
};

/**
 * 총괄 대시보드 조회
 */
export const getSuperAdminOverview = async (superAdminId: string, period: string, accessToken: string, baseDate?: string, week?: number, day?: number, includeGroup?: boolean): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'get',
            url: buildUrl(`${process.env.REACT_APP_SERVER}/v1/superadmins/dashboard/overview/${superAdminId}`, {
                period, baseDate,
                week: week != null ? String(week) : undefined,
                day: day != null ? String(day) : undefined,
                includeGroup: includeGroup ? 'true' : undefined,
            }),
            headers: { authorization: accessToken },
        });
        if (response.status === 200) return response;
    } catch (error: any) {
        switch (error.response?.status) {
            case 401: case 403: return { status: error.response.status };
            case 500: return { status: error.response.status };
        }
    }
};

/**
 * 매출 추이 조회 (차트용 — week/day 불필요, 항상 월 전체 시계열)
 */
export const getSuperAdminRevenueTrend = async (superAdminId: string, period: string, accessToken: string, baseDate?: string, includeGroup?: boolean): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'get',
            url: buildUrl(`${process.env.REACT_APP_SERVER}/v1/superadmins/dashboard/revenue-trend/${superAdminId}`, { period, baseDate, includeGroup: includeGroup ? 'true' : undefined }),
            headers: { authorization: accessToken },
        });
        if (response.status === 200) return response;
    } catch (error: any) {
        switch (error.response?.status) {
            case 401: case 403: return { status: error.response.status };
            case 500: return { status: error.response.status };
        }
    }
};

/**
 * 환불 분석/랭킹 조회
 */
export const getSuperAdminRefundAnalysis = async (
    superAdminId: string,
    period: string,
    accessToken: string,
    baseDate?: string,
    week?: number,
    day?: number,
    includeGroup?: boolean,
    includeFacility?: boolean,
): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'get',
            url: buildUrl(`${process.env.REACT_APP_SERVER}/v1/superadmins/dashboard/refund-analysis/${superAdminId}`, {
                period,
                baseDate,
                week: week != null ? String(week) : undefined,
                day: day != null ? String(day) : undefined,
                includeGroup: includeGroup ? 'true' : undefined,
                includeFacility: includeFacility ? 'true' : undefined,
            }),
            headers: { authorization: accessToken },
        });
        if (response.status === 200) return response;
    } catch (error: any) {
        switch (error.response?.status) {
            case 401: case 403: return { status: error.response.status };
            case 500: return { status: error.response.status };
        }
    }
};

/**
 * 센터별 강사 추이 조회 (차트용)
 */
export const getSuperAdminCenterTrend = async (superAdminId: string, centerId: string, period: string, metric: string, accessToken: string, baseDate?: string, includeGroup?: boolean): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'get',
            url: buildUrl(`${process.env.REACT_APP_SERVER}/v1/superadmins/dashboard/center-detail/${superAdminId}/${centerId}/trend`, { period, metric, baseDate, includeGroup: includeGroup ? 'true' : undefined }),
            headers: { authorization: accessToken },
        });
        if (response.status === 200) return response;
    } catch (error: any) {
        switch (error.response?.status) {
            case 401: case 403: return { status: error.response.status };
            case 500: return { status: error.response.status };
        }
    }
};

/**
 * 센터 상세 조회
 */
export const getSuperAdminCenterDetail = async (superAdminId: string, centerId: string, period: string, accessToken: string, baseDate?: string, week?: number, day?: number, includeGroup?: boolean): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'get',
            url: buildUrl(`${process.env.REACT_APP_SERVER}/v1/superadmins/dashboard/center-detail/${superAdminId}/${centerId}`, {
                period, baseDate,
                week: week != null ? String(week) : undefined,
                day: day != null ? String(day) : undefined,
                includeGroup: includeGroup ? 'true' : undefined,
            }),
            headers: { authorization: accessToken },
        });
        if (response.status === 200) return response;
    } catch (error: any) {
        switch (error.response?.status) {
            case 401: case 403: return { status: error.response.status };
            case 500: return { status: error.response.status };
        }
    }
};

export type SuperAdminReRegistrationSuccessMembersParams = {
    superAdminId: string;
    centerId: string;
    cohortMonth: string;
    status?: 'SUCCEEDED' | 'NOT_YET_RE_REGISTERED';
    /** 강사 근거일 때만. 없으면 지점 전체 */
    coachId?: string;
    page?: number;
    limit?: number;
};

/**
 * 재등록 성공률 산정 근거(회원 목록) — 슈퍼관리자 전용.
 * 응답 data 는 관리자웹 `/v1/centers/:centerId/re-registration-success/members` 와 같은 모양.
 * 🔴 search 파라미터는 서버 계약에 없다 — 보내지 않는다.
 * 404 = 권한 없음/지점 없음(구 서버라 라우트가 없어도 404) → `{ status: 404 }` 로 돌려 호출부가 구분한다.
 */
export const getSuperAdminReRegistrationSuccessMembers = async ({
    superAdminId,
    centerId,
    cohortMonth,
    status,
    coachId,
    page = 1,
    limit = 20,
    accessToken,
}: SuperAdminReRegistrationSuccessMembersParams & { accessToken: string }): Promise<any> => {
    const params = new URLSearchParams({ cohortMonth, page: String(page), limit: String(limit) });
    if (status) params.append('status', status);
    if (coachId) params.append('coachId', coachId);
    try {
        return await axios({
            method: 'get',
            url: `${process.env.REACT_APP_SERVER}/v1/superadmins/dashboard/re-registration-success/members/${superAdminId}/${centerId}?${params.toString()}`,
            headers: { authorization: accessToken },
        });
    } catch (error: any) {
        const statusCode = error.response?.status;
        if ([400, 401, 403, 404, 406, 500].includes(statusCode)) return { status: statusCode };
        throw error;
    }
};
